import {
  ClipboardListIcon as ClipboardListIconOutline,
  MapIcon as MapIconOutline,
} from "@heroicons/react/outline";
import {
  ClipboardListIcon as ClipboardListIconSolid,
  MapIcon as MapIconSolid,
} from "@heroicons/react/solid";
import { runInAction } from "mobx";
import { useMemo, useState } from "react";

import { WorkflowVersion } from "../../types";
import WorkflowCanvas from "../../WorkflowCanvas";
import { IWorkflow } from "../../WorkflowCanvas/workflow-store";
import WorkflowVersionAnalytics from "../../WorkflowVersionAnalytics";
import WorkflowVersionTracesSearchProvider from "../../WorkflowVersionAnalytics/WorkflowVersionTracesSearchProvider";
import WorkflowVersionSelector from "../WorkflowVersionSelector";

import TabNavigation from "@/components/ui/TabNavigation";
import { Button } from "@/components/ui/button";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface OverviewContentProps {
  showVersionList: boolean;
  versions: WorkflowVersion[];
  workflowRef: React.MutableRefObject<IWorkflow | undefined>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  observableWorkflowRef: any;
  setShowVersionList: (value: boolean) => void;
  refetchVersions: () => void;
  userToken: string;
  workflow: IWorkflow;
  workflowId: string;
  versionNumber: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  agentBuilderButton: any;
}

const OverviewContent = ({
  showVersionList,
  versions,
  workflowRef,
  observableWorkflowRef,
  setShowVersionList,
  refetchVersions,
  agentBuilderButton,
  userToken,
  workflow,
  workflowId,
  versionNumber,
}: OverviewContentProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { isEditDisabled } = useRbac();
  const cannotEditWorkflow = isEditDisabled(EntityType.WORKFLOW);

  const selectedVersion = useMemo(
    () => versions?.find(version => version.number === Number(versionNumber)),
    [versionNumber, versions],
  );

  return (
    <WorkflowVersionTracesSearchProvider
      workflowId={workflowId}
      workflowName={workflow.name || `Workflow ${workflowId}`}
      versionNumber={versionNumber}
      versionCreatedAt={selectedVersion?.created_at}
    >
      <div className="flex h-full flex-row gap-x-2">
        <div
          className={cn(
            "min-w-0 shrink-0 overflow-hidden pt-2 transition-[width] duration-300 ease-in-out",
            showVersionList ? "w-[360px]" : "w-12",
          )}
        >
          <WorkflowVersionSelector
            versions={versions}
            workflow={workflowRef.current || workflow}
            showVersionList={showVersionList}
            setShowVersionList={setShowVersionList}
            refreshVersions={refetchVersions}
            navigateToVersionLink={version =>
              `/workspace/${workflow.workspace_id}/workflow/${workflow.id}/version/${version}`
            }
            className="z-20 h-full w-full min-w-0 max-w-none"
          />
        </div>
        <div className="flex h-full flex-1 flex-col overflow-auto">
          <TabNavigation
            handleTabChange={setSelectedTabIndex}
            selectedIndex={selectedTabIndex}
            actionButtons={
              <div className="relative bottom-1 flex items-center gap-x-2">
                <BasicTooltip
                  content={
                    cannotEditWorkflow
                      ? "You don't have permission to edit this workflow"
                      : undefined
                  }
                  shouldShow={Boolean(cannotEditWorkflow)}
                >
                  <span>
                    <Button
                      onClick={agentBuilderButton.onClick}
                      variant={agentBuilderButton.variant}
                      size="sm"
                      aria-label="Edit"
                      disabled={cannotEditWorkflow}
                    >
                      {agentBuilderButton.icon}
                      {agentBuilderButton.label}
                    </Button>
                  </span>
                </BasicTooltip>
              </div>
            }
            tabs={[
              {
                id: 0,
                name: "Content",
                outlineIcon: <MapIconOutline className="h-4 w-4" />,
                solidIcon: <MapIconSolid className="h-4 w-4" />,
                content: (
                  <WorkflowCanvas
                    ref={ref => {
                      workflowRef.current = ref as IWorkflow;
                      runInAction(() => observableWorkflowRef.set(ref as IWorkflow));
                    }}
                    {...workflow}
                    readonly
                    userToken={userToken}
                  />
                ),
              },
              {
                id: 1,
                name: "Analytics & Logs",
                solidIcon: <ClipboardListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                outlineIcon: (
                  <ClipboardListIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />
                ),
                content: (
                  <WorkflowVersionAnalytics
                    workflowId={workflowId}
                    versionNumber={versionNumber}
                    versionCreatedAt={selectedVersion?.created_at}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>
    </WorkflowVersionTracesSearchProvider>
  );
};

export default OverviewContent;
